import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, FileDown, RefreshCw, Factory, Tag, CheckCircle2, Ban, Pencil } from "lucide-react";
import api, { formatApiError, downloadFromApi } from "../lib/api";
import PageHeader from "../components/PageHeader";
import RecordProductionModal from "../components/RecordProductionModal";
import LoadTagsModal from "../components/LoadTagsModal";
import PackingListsModal from "../components/PackingListsModal";
import AdminActionModal from "../components/AdminActionModal";
import FreightDataModal from "../components/FreightDataModal";
import IssuedLoadTagsPanel from "../components/IssuedLoadTagsPanel";
import UnusedPalletsCompletionModal from "../components/UnusedPalletsCompletionModal";
import EditOrderModal from "../components/EditOrderModal";
import { useAuth } from "../contexts/AuthContext";
import { fmtDate, fmtMoney, fmtPct } from "../lib/format";

function statusBadge(status) {
    if (status === "completed")
        return <span className="odr-badge odr-badge-completed">Completed</span>;
    if (status === "awaiting_shipment")
        return <span className="odr-badge odr-badge-awaiting">Awaiting Shipment</span>;
    return <span className="odr-badge odr-badge-open">Open</span>;
}

/** Round-13 LTL gate — reads from /freight-status (which is now backed by
 *  the unified `pallets` collection so it covers reserved + produced pallets
 *  and excludes voided ones automatically). */
function LtlFreightGate({ order, refreshKey, onCapture }) {
    const [status, setStatus] = useState(null);
    useEffect(() => {
        if (!order?.id || order.shipping_method !== "LTL") return;
        api.get(`/orders/${order.id}/freight-status`)
            .then((r) => setStatus(r.data))
            .catch((err) => console.error("Freight-status load failed", err));
    }, [order?.id, order?.shipping_method, refreshKey]);

    if (order?.shipping_method !== "LTL") return null;
    if (!status || status.complete) return null;
    return (
        <div
            className="text-sm border border-amber-300 bg-amber-50 text-amber-900 rounded-sm px-4 py-3 mb-4 flex items-start justify-between gap-3"
            data-testid="ltl-freight-gate"
        >
            <div className="flex items-start gap-3">
                <Ban className="w-5 h-5 mt-0.5 text-amber-700 shrink-0" />
                <div>
                    <div className="font-bold uppercase tracking-wider text-xs mb-1">
                        LTL Freight Data Required
                    </div>
                    <div>
                        {status.missing.length} of {status.total_pallets} pallet(s) on
                        this order are missing Width / Length / Height / Weight. A
                        Packing List cannot be generated until these are captured —
                        or the unused pallets are voided.
                    </div>
                </div>
            </div>
            <div className="flex gap-2 shrink-0">
                <button
                    type="button"
                    onClick={onCapture}
                    className="odr-btn text-xs font-semibold rounded-sm bg-amber-600 hover:bg-amber-700 text-white px-3"
                    data-testid="ltl-fix-freight"
                >
                    Enter Freight Data
                </button>
            </div>
        </div>
    );
}

export default function OrderDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [order, setOrder] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [rpEntries, setRpEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [regenerating, setRegenerating] = useState(false);
    const [rpModal, setRpModal] = useState(false);
    const [ltModal, setLtModal] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [plModal, setPlModal] = useState(false);
    const [plCustomer, setPlCustomer] = useState(null);
    const [voidOrderOpen, setVoidOrderOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [voidRpTarget, setVoidRpTarget] = useState(null); // RP row being voided
    const [voidDocTarget, setVoidDocTarget] = useState(null); // Doc row being voided
    const [freightOpen, setFreightOpen] = useState(false);
    const [freightRefreshKey, setFreightRefreshKey] = useState(0);
    const [unusedPallets, setUnusedPallets] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [o, d, rp] = await Promise.all([
                api.get(`/orders/${id}`),
                api.get(`/documents`, { params: { order_id: id, type: "order_confirmation" } }),
                api.get(`/orders/${id}/rp`),
            ]);
            setOrder(o.data);
            setDocuments(d.data);
            setRpEntries(rp.data);
            setError("");
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    const completeOrder = useCallback(async (voidUnusedPallets = false) => {
        // V1#11 — Mark Order as Completed (standalone). Server validates
        // tolerance + production totals; surfaces blocking issues if any.
        // Round-29: if reserved pallets remain, server returns 409 with the
        // list and we open the unused-pallets modal for confirmation.
        if (!voidUnusedPallets && !unusedPallets) {
            if (!window.confirm(
                "Mark this order as COMPLETED?\n\n" +
                "Tolerance and production totals will be re-validated server-side. " +
                "If any line is outside tolerance without an RP override, completion will be blocked."
            )) return;
        }
        setCompleting(true);
        setError("");
        try {
            const { data } = await api.post(
                `/orders/${id}/complete`,
                { void_unused_pallets: voidUnusedPallets },
            );
            setUnusedPallets(null);
            await load();
            const voidedNote =
                data.voided_unused_pallets && data.voided_unused_pallets.length
                    ? `\nUnused tags voided: ${data.voided_unused_pallets.length}`
                    : "";
            window.alert(
                `Order completed.\n\n` +
                `Ordered: ${data.totals.ordered}\n` +
                `Produced: ${data.totals.produced}\n` +
                `Shipped: ${data.totals.shipped}` +
                voidedNote,
            );
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (
                detail && typeof detail === "object" &&
                detail.error === "UNUSED_PALLETS_EXIST"
            ) {
                setUnusedPallets(detail.unused_pallets || []);
            } else if (detail && typeof detail === "object" && detail.issues) {
                setError(
                    "Completion blocked:\n• " + detail.issues.join("\n• ")
                );
            } else {
                setError(formatApiError(detail) || err.message);
            }
        } finally {
            setCompleting(false);
        }
    }, [id, load, unusedPallets]);

    useEffect(() => {
        load();
    }, [load]);

    const regenerate = async () => {
        setRegenerating(true);
        try {
            await api.post(`/orders/${id}/regenerate-confirmation`);
            await load();
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setRegenerating(false);
        }
    };

    const downloadDoc = async (doc) => {
        try {
            await downloadFromApi(`/documents/${doc.id}/download`, doc.filename);
        } catch (err) {
            console.error("downloadDoc failed", err);
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        }
    };

    if (loading) {
        return (
            <div className="text-sm text-gray-500" data-testid="order-detail-loading">
                Loading…
            </div>
        );
    }
    if (!order) {
        return (
            <div className="text-sm text-red-600" data-testid="order-detail-error">
                {error || "Order not found"}
            </div>
        );
    }

    return (
        <div data-testid="order-detail-page">
            <Link
                to="/orders"
                className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-2"
            >
                <ChevronLeft className="w-4 h-4" /> Back to Orders
            </Link>
            <PageHeader
                title={
                    <span className="odr-mono">{order.internal_number}</span>
                }
                subtitle={`${order.customer_name_snapshot} · PO ${order.customer_po}`}
                actions={
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setRpModal(true)}
                            disabled={order.status === "completed"}
                            className="odr-btn inline-flex items-center gap-2 rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4 disabled:opacity-50"
                            data-testid="open-rp-modal"
                        >
                            <Factory className="w-4 h-4" /> Record Production
                        </button>
                        <button
                            onClick={() => setLtModal(true)}
                            disabled={order.status === "completed"}
                            className="odr-btn inline-flex items-center gap-2 rounded-sm border border-gray-300 px-4 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                            data-testid="open-lt-modal"
                        >
                            <Tag className="w-4 h-4" /> Load Tags
                        </button>
                        <button
                            onClick={regenerate}
                            disabled={regenerating}
                            className="odr-btn inline-flex items-center gap-2 rounded-sm border border-gray-300 px-4 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                            data-testid="regenerate-confirmation"
                            title="Generate a new version of the Order Confirmation"
                        >
                            <RefreshCw className="w-4 h-4" />{" "}
                            {regenerating ? "Generating…" : "Regenerate OC"}
                        </button>
                        <button
                            onClick={async () => {
                                try {
                                    const c = await api.get(`/customers/${order.customer_id}`);
                                    setPlCustomer(c.data);
                                } catch (err) {
                                    console.error("Failed to load customer for PL modal", err);
                                    setPlCustomer({ contacts: [] });
                                }
                                setPlModal(true);
                            }}
                            disabled={order.status === "completed"}
                            className="odr-btn inline-flex items-center gap-2 rounded-sm border border-gray-300 px-4 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                            data-testid="open-pl-modal"
                            title={order.status === "completed" ? "Order completed — PL closed" : "Generate Packing List"}
                        >
                            <FileDown className="w-4 h-4" /> Packing List
                        </button>
                        <button
                            onClick={() => setEditOpen(true)}
                            disabled={order.status === "completed" || order.voided}
                            className="odr-btn inline-flex items-center gap-2 rounded-sm border border-gray-300 px-4 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                            data-testid="edit-order-open"
                            title="Edit order header and lines"
                        >
                            <Pencil className="w-4 h-4" /> Edit Order
                        </button>
                        <button
                            onClick={() => completeOrder(false)}
                            disabled={
                                order.status === "completed" || completing
                            }
                            className="odr-btn inline-flex items-center gap-2 rounded-sm border border-green-700 text-green-700 hover:bg-green-50 px-4 text-sm font-semibold disabled:opacity-50"
                            data-testid="complete-order-btn"
                            title="Mark this order as completed (validates tolerance + production totals)"
                        >
                            <CheckCircle2 className="w-4 h-4" />{" "}
                            {completing ? "Completing…" : "Complete Order"}
                        </button>
                        {isAdmin && !order.voided && order.status !== "completed" && (
                            <button
                                onClick={() => setVoidOrderOpen(true)}
                                className="odr-btn inline-flex items-center gap-2 rounded-sm border border-red-700 text-red-700 hover:bg-red-50 px-4 text-sm font-semibold"
                                data-testid="void-order-btn"
                                title="Admin only — void this order (reason required, audited)"
                            >
                                <Ban className="w-4 h-4" /> Void Order
                            </button>
                        )}
                    </div>
                }
            />

            {error && (
                <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2 mb-4">
                    {error}
                </div>
            )}

            {order.voided && (
                <div
                    className="text-sm border border-red-300 bg-red-50 text-red-800 rounded-sm px-4 py-3 mb-4 flex items-start gap-3"
                    data-testid="order-voided-banner"
                >
                    <Ban className="w-5 h-5 mt-0.5 shrink-0" />
                    <div>
                        <div className="font-bold uppercase tracking-wider text-xs mb-1">
                            Order Voided
                        </div>
                        <div>
                            Voided by <span className="odr-mono">{order.voided_by}</span>{" "}
                            on {fmtDate(order.voided_at)} ·{" "}
                            <span className="italic">&ldquo;{order.void_reason}&rdquo;</span>
                        </div>
                    </div>
                </div>
            )}

            <LtlFreightGate
                order={order}
                refreshKey={freightRefreshKey}
                onCapture={() => setFreightOpen(true)}
            />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-white border border-gray-200 rounded-sm p-3">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
                        Status
                    </div>
                    <div className="mt-2">{statusBadge(order.status)}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-3">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
                        PO Date
                    </div>
                    <div className="mt-1 text-sm font-medium">{fmtDate(order.po_date)}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-3">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
                        ETA
                    </div>
                    <div className="mt-1 text-sm font-medium">{fmtDate(order.eta)}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-3">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
                        Blind Shipment
                    </div>
                    <div className="mt-2">
                        {order.blind_shipment ? (
                            <span className="odr-badge odr-badge-past">Yes</span>
                        ) : (
                            <span className="text-sm text-gray-400">No</span>
                        )}
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-3">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
                        Production Type
                    </div>
                    <div className="mt-1">
                        <select
                            value={order.production_type || "in_house"}
                            onChange={async (e) => {
                                const newValue = e.target.value;
                                try {
                                    await api.patch(`/orders/${order.id}`, {
                                        production_type: newValue,
                                    });
                                    await load();
                                } catch (err) {
                                    setError(
                                        formatApiError(
                                            err?.response?.data?.detail,
                                        ) || err.message,
                                    );
                                }
                            }}
                            className="odr-input w-full rounded-sm border border-gray-300 px-2 text-sm bg-white"
                            data-testid="order-detail-production-type"
                        >
                            <option value="in_house">In-House</option>
                            <option value="farm_out">Farmed Out</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto mb-6">
                <table className="odr-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Product</th>
                            <th>Category</th>
                            <th>Type</th>
                            <th className="text-right">Qty</th>
                            <th className="text-right">Produced</th>
                            <th className="text-right">Remaining</th>
                            <th className="text-right">Price</th>
                            <th className="text-right">Line Value</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(order.lines || []).map((l) => {
                            const remaining = Math.max(
                                (l.qty_ordered || 0) - (l.produced_qty || 0),
                                0,
                            );
                            return (
                                <tr key={l.id} data-testid={`detail-line-${l.line_no}`}>
                                    <td className="odr-mono">{l.line_no}</td>
                                    <td>{l.product_snapshot?.name}</td>
                                    <td className="text-gray-700">
                                        {l.product_snapshot?.category_name || "—"}
                                    </td>
                                    <td className="text-xs">
                                        {l.product_snapshot?.production_type === "farm_out"
                                            ? "Farm-Out"
                                            : "In-House"}
                                    </td>
                                    <td className="text-right odr-mono">
                                        {Number(l.qty_ordered).toLocaleString()}
                                    </td>
                                    <td className="text-right odr-mono">
                                        {Number(l.produced_qty || 0).toLocaleString()}
                                    </td>
                                    <td className="text-right odr-mono">
                                        {remaining.toLocaleString()}
                                    </td>
                                    <td className="text-right odr-mono">{fmtMoney(l.price)}</td>
                                    <td className="text-right odr-mono">{fmtMoney(l.line_value)}</td>
                                    <td>
                                        {l.active ? (
                                            <span className="odr-badge odr-badge-open">Active</span>
                                        ) : (
                                            <span className="odr-badge odr-badge-past">Voided</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        <tr className="bg-gray-50">
                            <td colSpan={4} className="text-right font-semibold">
                                Order Total
                            </td>
                            <td className="text-right font-bold odr-mono">
                                {Number(order.totals?.qty_ordered || 0).toLocaleString()}
                            </td>
                            <td className="text-right font-bold odr-mono">
                                {Number(order.production?.qty_produced || 0).toLocaleString()}
                            </td>
                            <td colSpan={2}></td>
                            <td className="text-right font-bold odr-mono">
                                {fmtMoney(order.totals?.value)}
                            </td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h3 className="text-xs uppercase tracking-wider font-semibold text-gray-600 mb-2">
                Record Production History
            </h3>
            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto mb-6">
                <table className="odr-table" data-testid="rp-history-table">
                    <thead>
                        <tr>
                            <th>RP #</th>
                            <th>Date</th>
                            <th>Product</th>
                            <th className="text-right">Pallets</th>
                            <th className="text-right">Units</th>
                            <th className="text-right">Variance</th>
                            <th>Status</th>
                            <th>User</th>
                            <th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rpEntries.length === 0 && (
                            <tr>
                                <td colSpan={9} className="text-center text-gray-500 py-4">
                                    No production recorded yet.
                                </td>
                            </tr>
                        )}
                        {rpEntries.map((rp) => (
                            <tr
                                key={rp.id}
                                data-testid={`rp-row-${rp.rp_number}`}
                                className={rp.voided ? "opacity-40 line-through" : ""}
                            >
                                <td className="odr-mono text-xs font-semibold">
                                    {rp.rp_number}
                                    {rp.voided && (
                                        <span
                                            className="ml-2 odr-badge odr-badge-past"
                                            title={`Voided by ${rp.voided_by}: ${rp.void_reason}`}
                                        >
                                            VOIDED
                                        </span>
                                    )}
                                </td>
                                <td className="text-xs">{fmtDate(rp.production_date)}</td>
                                <td className="text-sm">{rp.product_name_snapshot}</td>
                                <td className="text-right odr-mono">{rp.total_pallets}</td>
                                <td className="text-right odr-mono">
                                    {Number(rp.total_units).toLocaleString()}
                                </td>
                                <td className="text-right odr-mono">
                                    {fmtPct(rp.variance_pct)}
                                </td>
                                <td>
                                    {rp.tolerance_status === "ok" && (
                                        <span className="odr-badge odr-badge-open">OK</span>
                                    )}
                                    {rp.tolerance_status === "overrun" && (
                                        <span className="odr-badge odr-badge-past">
                                            Overrun {rp.tolerance_override ? "· override" : ""}
                                        </span>
                                    )}
                                    {rp.tolerance_status === "underrun" && (
                                        <span className="odr-badge odr-badge-near">
                                            Underrun {rp.tolerance_override ? "· override" : ""}
                                        </span>
                                    )}
                                </td>
                                <td className="text-xs">{rp.created_by}</td>
                                <td className="text-right">
                                    {isAdmin && !rp.voided && (
                                        <button
                                            onClick={() => setVoidRpTarget(rp)}
                                            className="text-xs text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
                                            data-testid={`void-rp-${rp.rp_number}`}
                                            title="Admin only — void this RP record (reason required)"
                                        >
                                            <Ban className="w-3.5 h-3.5" /> Void
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <IssuedLoadTagsPanel
                orderId={order.id}
                isLtl={order.shipping_method === "LTL"}
                isAdmin={isAdmin}
                onChanged={() => {
                    setFreightRefreshKey((k) => k + 1);
                    load();
                }}
            />

            <h3 className="text-xs uppercase tracking-wider font-semibold text-gray-600 mb-2">
                Document History
            </h3>
            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                <table className="odr-table" data-testid="documents-table">
                    <thead>
                        <tr>
                            <th>Document #</th>
                            <th>Filename</th>
                            <th>Type</th>
                            <th>Version</th>
                            <th>Generated</th>
                            <th>By</th>
                            <th className="text-right">Size</th>
                            <th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {documents.length === 0 && (
                            <tr>
                                <td colSpan={8} className="text-center text-gray-500 py-4">
                                    No documents yet.
                                </td>
                            </tr>
                        )}
                        {documents.map((d) => (
                            <tr
                                key={d.id}
                                data-testid={`doc-row-${d.document_number}`}
                                className={d.voided ? "opacity-40 line-through" : ""}
                            >
                                <td className="odr-mono text-xs font-semibold">
                                    {d.document_number}
                                    {d.voided && (
                                        <span
                                            className="ml-2 odr-badge odr-badge-past"
                                            title={`Voided by ${d.voided_by}: ${d.void_reason}`}
                                        >
                                            VOIDED
                                        </span>
                                    )}
                                </td>
                                <td className="odr-mono text-xs">{d.filename}</td>
                                <td className="text-xs">{d.type}</td>
                                <td className="odr-mono">v{d.version}</td>
                                <td className="text-xs">{fmtDate(d.generated_at)}</td>
                                <td className="text-xs">{d.generated_by}</td>
                                <td className="text-right odr-mono text-xs">
                                    {Math.round((d.size_bytes || 0) / 1024).toLocaleString()} KB
                                </td>
                                <td className="text-right">
                                    <div className="inline-flex items-center gap-3 justify-end">
                                        <button
                                            onClick={() => downloadDoc(d)}
                                            className="text-sm text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
                                            data-testid={`download-${d.id}`}
                                        >
                                            <FileDown className="w-3.5 h-3.5" /> Download
                                        </button>
                                        {isAdmin && !d.voided && (
                                            <button
                                                onClick={() => setVoidDocTarget(d)}
                                                className="text-xs text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
                                                data-testid={`void-doc-${d.document_number}`}
                                                title="Admin only — void this document (reason required)"
                                            >
                                                <Ban className="w-3.5 h-3.5" /> Void
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {rpModal && (
                <RecordProductionModal
                    order={order}
                    onClose={() => setRpModal(false)}
                    onSaved={async () => {
                        setRpModal(false);
                        await load();
                        // Auto-open freight modal after RP for LTL orders.
                        if (order?.shipping_method === "LTL") {
                            setFreightOpen(true);
                            setFreightRefreshKey((k) => k + 1);
                        }
                    }}
                />
            )}

            {freightOpen && (
                <FreightDataModal
                    open={freightOpen}
                    order={order}
                    onClose={() => setFreightOpen(false)}
                    onSaved={async () => {
                        setFreightRefreshKey((k) => k + 1);
                        await load();
                    }}
                />
            )}

            {unusedPallets && (
                <UnusedPalletsCompletionModal
                    pallets={unusedPallets}
                    busy={completing}
                    onCancel={() => setUnusedPallets(null)}
                    onReview={() => {
                        setUnusedPallets(null);
                        const el = document.querySelector(
                            "[data-testid='issued-load-tags-panel']",
                        );
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    onVoidAndComplete={() => completeOrder(true)}
                />
            )}

            {ltModal && (
                <LoadTagsModal
                    order={order}
                    onClose={() => setLtModal(false)}
                    onGenerated={async () => {
                        setLtModal(false);
                        await load();
                        // V1 Round-13 — advance LTs now create reserved pallet
                        // records. Auto-open the freight modal for LTL orders so
                        // the operator can capture LTL dimensions immediately.
                        if (order?.shipping_method === "LTL") {
                            setFreightOpen(true);
                            setFreightRefreshKey((k) => k + 1);
                        }
                    }}
                />
            )}

            {plModal && plCustomer && (
                <PackingListsModal
                    order={order}
                    customer={plCustomer}
                    onClose={() => setPlModal(false)}
                    onGenerated={async () => {
                        setPlModal(false);
                        await load();
                    }}
                />
            )}

            <AdminActionModal
                open={voidOrderOpen}
                title={`Void Order ${order.internal_number}`}
                description={
                    <span>
                        This order will be marked <strong>voided</strong>. It will
                        disappear from the operational order list (visible only when
                        &ldquo;Show voided&rdquo; is enabled) and stop counting toward Dashboard
                        KPIs. The internal number <span className="odr-mono">{order.internal_number}</span>{" "}
                        is permanent and will never be reused.
                    </span>
                }
                confirmLabel="Void Order"
                onCancel={() => setVoidOrderOpen(false)}
                onConfirm={async (reason) => {
                    await api.post(`/orders/${order.id}/void`, { reason });
                    setVoidOrderOpen(false);
                    await load();
                }}
                testIdPrefix="void-order"
            />

            <AdminActionModal
                open={!!voidRpTarget}
                title={voidRpTarget ? `Void RP ${voidRpTarget.rp_number}` : ""}
                description={
                    voidRpTarget ? (
                        <span>
                            Voiding this RP will subtract{" "}
                            <strong className="odr-mono">
                                {Number(voidRpTarget.total_units || 0).toLocaleString()}
                            </strong>{" "}
                            units from the order&rsquo;s produced total and refresh the
                            tolerance status. The RP number{" "}
                            <span className="odr-mono">{voidRpTarget.rp_number}</span>{" "}
                            will never be reused.
                        </span>
                    ) : null
                }
                confirmLabel="Void RP"
                onCancel={() => setVoidRpTarget(null)}
                onConfirm={async (reason) => {
                    await api.post(
                        `/orders/${order.id}/rp/${voidRpTarget.id}/void`,
                        { reason },
                    );
                    setVoidRpTarget(null);
                    await load();
                }}
                testIdPrefix="void-rp"
            />

            <AdminActionModal
                open={!!voidDocTarget}
                title={voidDocTarget ? `Void document ${voidDocTarget.document_number}` : ""}
                description={
                    voidDocTarget ? (
                        <span>
                            The PDF will remain in the archive for audit purposes but
                            will be marked <strong>VOIDED</strong> and{" "}
                            <strong>reprint will be blocked</strong>. Generate a new
                            version of the {voidDocTarget.type?.replace("_", " ")} if a
                            corrected copy is needed.
                        </span>
                    ) : null
                }
                confirmLabel="Void Document"
                onCancel={() => setVoidDocTarget(null)}
                onConfirm={async (reason) => {
                    await api.post(`/documents/${voidDocTarget.id}/void`, { reason });
                    setVoidDocTarget(null);
                    await load();
                }}
                testIdPrefix="void-doc"
            />

            {editOpen && (
                <EditOrderModal
                    order={order}
                    onClose={() => setEditOpen(false)}
                    onSaved={() => load()}
                />
            )}
        </div>
    );
}
